/** TanStack Query wrappers for the caller's clients. */

import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ClientCreate,
  ClientResponse,
  ClientSummary,
  ClientUpdate,
} from "@patent-studio/api-types";
import {
  createClient,
  deleteClient,
  findInventors,
  listClients,
  putClientLogo,
  removeClientLogo,
  updateClient,
} from "@/services/clients";
import { CLIENTS_KEY, TASKS_KEY } from "./query-keys";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/**
 * How long the list is trusted before it is read again. A logo is served as a
 * link signed for twelve hours, but the credentials that sign it rotate every
 * few hours and take the link with them, so the list is not trusted for long.
 */
export const CLIENT_LIST_STALE_MS = 30 * 60 * 1000;

/** `enabled` holds the read back until something asks, as the palette does until it opens. */
export function useClients({ enabled = true } = {}) {
  return useQuery({
    queryKey: CLIENTS_KEY,
    queryFn: listClients,
    staleTime: CLIENT_LIST_STALE_MS,
    refetchOnWindowFocus: true,
    enabled,
  });
}

/** How long the type-ahead waits after a keystroke before it asks, so a word typed asks once. */
const SETTLE_MS = 200;

/**
 * The inventors on record whose name holds what is typed, asked for once the words settle and
 * only from two characters. Keyed under the clients, so a new draft brings its inventors on.
 *
 * While the words settle or their answer is on its way, the last answer stays and `pending`
 * says so, so a field does not say "no match" for words nobody has answered yet.
 */
export function useInventorMatches(text: string, clientId: string | null) {
  const [settled, setSettled] = useState(text);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(text);
    }, SETTLE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [text]);
  const asked = settled.trim();
  const query = useQuery({
    queryKey: [...CLIENTS_KEY, "inventors", clientId, asked],
    queryFn: () => findInventors(asked, clientId),
    enabled: asked.length >= 2,
    placeholderData: keepPreviousData,
  });
  const pending =
    asked !== text.trim() || query.isPlaceholderData || query.isLoading;
  return { inventors: query.data?.inventors ?? [], pending };
}

/** One client off the list the landing page already holds, rather than a read of its own. */
export function useClientFromList(clientId: string | null | undefined) {
  const list = useClients();
  const client: ClientSummary | undefined = list.data?.clients.find(
    (c) => c.client_id === clientId,
  );
  return { ...list, client };
}

export interface NewClient {
  details: ClientCreate;
  /** Stored once the client exists, since a logo belongs to a client that has an id. */
  logo: File | null;
}

/**
 * Adds a client, then its logo. A logo the server refuses leaves the client in
 * place without one, which is reported rather than undone: the record is what
 * the attorney asked for, and the picture can be added again.
 */
export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.clientCreate,
    mutationFn: async ({
      details,
      logo,
    }: NewClient): Promise<{
      client: ClientResponse;
      logoRefused: unknown;
    }> => {
      const client = await createClient(details);
      if (logo === null) return { client, logoRefused: null };
      try {
        return {
          client: await putClientLogo(client.client_id, logo),
          logoRefused: null,
        };
      } catch (refusal) {
        return { client, logoRefused: refusal };
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

/** Writes the fields sent and nothing else; the key contact one field at a time. */
export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.clientUpdate,
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: string;
      body: ClientUpdate;
    }) => updateClient(clientId, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

/** Replaces the client's logo with a file, or takes it off with null. */
export function useClientLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.clientLogo,
    mutationFn: async ({
      clientId,
      file,
    }: {
      clientId: string;
      file: File | null;
    }): Promise<void> => {
      if (file === null) await removeClientLogo(clientId);
      else await putClientLogo(clientId, file);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

/** Deletes the client and everything under it; its drafts' own reads go with it. */
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.clientDelete,
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
}
